'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { cn } from '@kit/ui/utils';

import { useIsUserOnline } from '../hooks/use-presence';

interface AvatarWithStatusProps {
  userId: string | null | undefined;
  src?: string | null;
  name: string;
  fallback?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showStatus?: boolean;
  className?: string;
}

const sizeClasses = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
} as const;

const dotSizeClasses = {
  xs: 'h-2 w-2 border',
  sm: 'h-2.5 w-2.5 border-[1.5px]',
  md: 'h-3 w-3 border-2',
  lg: 'h-3.5 w-3.5 border-2',
} as const;

/**
 * Avatar component with online status indicator
 *
 * Displays a user's avatar with a green/gray status dot
 * indicating their online/offline status.
 */
export function AvatarWithStatus({
  userId,
  src,
  name,
  fallback,
  size = 'md',
  showStatus = true,
  className,
}: AvatarWithStatusProps) {
  const isOnline = useIsUserOnline(userId);

  // Generate fallback from name if not provided
  const avatarFallback =
    fallback ||
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className="relative inline-flex shrink-0">
      <Avatar className={cn(sizeClasses[size], className)}>
        <AvatarImage src={src || undefined} alt={name} />
        <AvatarFallback className="text-xs">{avatarFallback}</AvatarFallback>
      </Avatar>
      {showStatus && (
        <span
          className={cn(
            'border-background absolute right-0 bottom-0 rounded-full',
            dotSizeClasses[size],
            isOnline ? 'bg-green-500' : 'bg-gray-400',
          )}
          aria-label={isOnline ? 'Online' : 'Offline'}
        />
      )}
    </div>
  );
}
